import { Box, CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import { sumBy } from 'lodash-es';
import { Fragment, useMemo, type JSX } from 'react';
import { SmallerTextInline } from 'src/components/common/smaller-text-inline/SmallerTextInline';
import {
    useLocale,
    getLocaleNumberFormatNoDecimals,
    formatCurrencyNoDecimalsIfPossible,
} from '@repo/i18n';
import { currencyAtom } from '@repo/widget-utils/currencyAtom';
import { TicketOptionWithQuantity, Currency, CurrencyType, TicketType } from '@repo/types';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { useAtom } from 'ximple/atoms';
import { useBookingContext } from 'src/widgets/BookingContext';

interface IProps {
    title: string;
    toggleButton?: JSX.Element;
    onClick?(): void;
    justifyTitle?: 'space-between' | 'center';
    addMarginRightToHeader?: boolean;
    expandArrowInside?: boolean;
    priceProps?: BookingHeaderPriceProps;
    expanded?: boolean;
}

const CurrencyCost = ({
    fromPrice,
    totalPrice,
    currencyType,
    currency,
    isLoadingPrices,
    loadingSize,
    hasSelectedTickets,
}: CurrencyCostProps) => {
    const { t, locale } = useLocale();
    const customizations = useCustomizations();
    const price = getLocaleNumberFormatNoDecimals(locale, fromPrice ? fromPrice : totalPrice);

    if (isLoadingPrices) {
        return (
            <Box component="span" fontWeight={customizations.boldFontWeight}>
                <CircularProgress
                    size={loadingSize}
                    sx={{ color: customizations.bookingWidgetColorContrast }}
                ></CircularProgress>
            </Box>
        );
    }

    if (currencyType === 'money') {
        // If there are no selected tickets, we don't want to show 0 as the actual price
        if (!hasSelectedTickets && !fromPrice) return null;

        return (
            <Fragment>
                <Grid component="span" fontSize="1rem" fontWeight={customizations.boldFontWeight}>
                    <SmallerTextInline size="60%">
                        <span>{fromPrice && t.from}</span>&nbsp;&nbsp;
                    </SmallerTextInline>
                    <Box component="span" fontWeight={customizations.boldFontWeight}>
                        {formatCurrencyNoDecimalsIfPossible(
                            locale,
                            currency,
                            fromPrice ? fromPrice : totalPrice,
                        )}
                    </Box>
                </Grid>
            </Fragment>
        );
    } else if (currencyType === 'credits')
        return (
            <Fragment>
                <Box component="span" fontWeight={customizations.boldFontWeight}>
                    {price}
                </Box>
                <SmallerTextInline size="60%">
                    &nbsp;&nbsp;{t.credit.num(totalPrice)}
                </SmallerTextInline>
            </Fragment>
        );
    return (
        <Fragment>
            <Box component="span" fontWeight={customizations.boldFontWeight}>
                {t.free}
            </Box>
        </Fragment>
    );
};

export function BookingHeader(props: IProps): JSX.Element {
    const {
        title,
        toggleButton,
        onClick,
        justifyTitle = 'space-between',
        addMarginRightToHeader,
        expandArrowInside = false,
        priceProps,
        expanded,
    } = props;
    const customizations = useCustomizations();
    const theme = useTheme();
    const { t } = useLocale();

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
        }
    };

    const ariaLabel =
        expanded !== undefined ? `${title}, ${expanded ? t.expanded : t.collapsed}` : title;

    return (
        <Fragment>
            <Grid
                container
                component="button"
                tabIndex={0}
                justifyContent={justifyTitle}
                pr={addMarginRightToHeader ? `${theme.spacing(8)} !important` : ''}
                padding={theme.spacing(expandArrowInside ? 4 : 3, 4, 0, 4)}
                mt={0}
                sx={{
                    cursor: 'pointer',
                    border: 'none',
                    background: 'transparent',
                    textAlign: 'left',
                    display: 'flex',
                    width: '100%',
                    boxSizing: 'border-box',
                    '&:focus': {
                        outline: `2px solid ${customizations.bookingWidgetPrimaryColor}`,
                        outlineOffset: '2px',
                    },
                }}
                onClick={onClick}
                onKeyDown={handleKeyDown}
                aria-label={ariaLabel}
                aria-expanded={expanded}
            >
                <Typography
                    component="h2"
                    variant="h4"
                    color={customizations.bookingWidgetHeaderColor}
                >
                    {title}
                </Typography>
                {priceProps && !priceProps.hidePrice && (
                    <Grid>
                        <Grid container alignItems="flex-end">
                            <Typography
                                component="span"
                                variant="h4"
                                color={customizations.bookingWidgetHeaderColor}
                            >
                                <CurrencyCost
                                    fromPrice={priceProps.fromPrice}
                                    totalPrice={priceProps.totalPrice}
                                    currencyType={priceProps.currencyType}
                                    currency={priceProps.currency}
                                    isLoadingPrices={priceProps.isLoadingPrices}
                                    loadingSize={customizations.h4Size}
                                    hasSelectedTickets={priceProps.hasSelectedTickets}
                                />
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </Grid>
            {toggleButton}
        </Fragment>
    );
}

function getCurrencyTypeFromQuantities(quantities?: TicketOptionWithQuantity[]): CurrencyType {
    const isMembership = quantities?.some((q) => q.membership);
    const isCredit = quantities?.some(
        (q) =>
            q.membership &&
            q.membership.some((m) => m.valueCardUsages.some((vcu) => vcu.creditCost > 0)),
    );
    const isCurrency =
        !isMembership || quantities?.some((q) => q.membership?.some((m) => !!m.currencyCost));
    if (!isCurrency && !isCredit) return 'membership';
    else if (isCredit) return 'credits';
    else return 'money';
}

function useTotalPrice(quantities?: TicketOptionWithQuantity[], selectedTicketType?: TicketType) {
    return useMemo(() => {
        if (!quantities) return { money: 0, credits: 0 };

        // For accommodation (Visbook): price is the authoritative total returned by
        // Visbook for the current units+guests+dates configuration. The quantities
        // array carries price: 0 on each ticket option (rooms/guests are counts,
        // not per-line prices), so sum(price*quantity) would always be 0.
        // usePriceQuantities also reduces accommodation to a single fake entry with
        // quantity=1, so we can't recover the total from quantities alone.
        const isAccommodation =
            quantities[0]?.productInstances?.[0]?.product?.type === 'accommodation';
        if (isAccommodation && selectedTicketType) {
            return { money: selectedTicketType.price ?? 0, credits: 0 };
        }

        return quantities.reduce(
            (acc, val) => {
                if (val.membership) {
                    acc.credits +=
                        sumBy(
                            val.membership.flatMap((m) => m.valueCardUsages),
                            'creditCost',
                        ) / 100;
                    acc.money += sumBy(val.membership, (m) => m.currencyCost?.price ?? 0) / 100;
                    return acc;
                } else {
                    acc.money += val.price * val.quantity;
                    return acc;
                }
            },
            { money: 0, credits: 0 },
        );
    }, [quantities, selectedTicketType]);
}

type CurrencyCostProps = {
    fromPrice: number | null | undefined;
    totalPrice: number;
    currencyType: CurrencyType;
    currency: Currency;
    isLoadingPrices: boolean;
    loadingSize: string;
    hasSelectedTickets: boolean;
};

export function usePricePropsFromQuantitiesInBookingContext(
    quantities: TicketOptionWithQuantity[] | undefined,
    fromPrice: number | null | undefined,
) {
    const { isLoadingPrices, visible, hasChangedQuantities, selectedTicketType } =
        useBookingContext();

    const fromPriceForBookingHeader = useMemo(() => {
        const showFromPrice = fromPrice && !visible && !hasChangedQuantities;
        return showFromPrice ? fromPrice : null;
    }, [visible, hasChangedQuantities, fromPrice]);

    return usePricePropsFromQuantities(
        quantities,
        fromPriceForBookingHeader,
        false,
        isLoadingPrices,
        selectedTicketType,
    );
}

export function usePricePropsFromQuantities(
    quantities: TicketOptionWithQuantity[] | undefined,
    fromPrice: number | null | undefined,
    hidePrice: boolean | undefined,
    isLoadingPrices: boolean,
    selectedTicketType?: TicketType,
) {
    const [currency] = useAtom(currencyAtom);
    const totalPrice = useTotalPrice(quantities, selectedTicketType);

    return useMemo(() => {
        if (!quantities) return undefined;

        const currencyType = getCurrencyTypeFromQuantities(quantities);

        // For accommodation the rooms ticket option carries the user's selection;
        // treat any selection as "selected" so the header switches from "from" to
        // the total price returned by Visbook.
        const isAccommodation =
            quantities[0]?.productInstances?.[0]?.product?.type === 'accommodation';
        const hasSelectedTickets = isAccommodation
            ? !!selectedTicketType
            : (quantities?.some((q) => q.quantity > 0) ?? false);
        const totalPriceValue = currencyType === 'money' ? totalPrice.money : totalPrice.credits;

        const priceProps: BookingHeaderPriceProps = {
            fromPrice: fromPrice,
            hidePrice: hidePrice,
            isLoadingPrices: isLoadingPrices,
            currency: currency,
            currencyType: currencyType,
            totalPrice: totalPriceValue,
            hasSelectedTickets: hasSelectedTickets,
        };

        return priceProps;
    }, [
        currency,
        fromPrice,
        hidePrice,
        isLoadingPrices,
        quantities,
        selectedTicketType,
        totalPrice.credits,
        totalPrice.money,
    ]);
}

export type BookingHeaderPriceProps = {
    fromPrice?: number | null;
    hidePrice?: boolean;
    isLoadingPrices: boolean;
    totalPrice: number;
    currencyType: CurrencyType;
    currency: Currency;
    hasSelectedTickets: boolean;
};
